import type { RefObject } from 'react'
import { create } from 'zustand'

/** Where the pre-check connection test is: not run, running, or its graded result. */
export type NetworkCheckStatus = 'idle' | 'running' | 'good' | 'warn' | 'error'
import { devtools } from 'zustand/middleware'

interface AudioSetup {
    isMicCompleted: boolean
    isSpeakerCompleted: boolean
    selectedMic: string | null
    selectedSpeaker: string | null
    micGain: number
    rnnoiseEnabled: boolean
}

interface VideoSetup {
    isCameraCompleted: boolean
    selectedCamera: string | null
    stream: RefObject<MediaStream | null>
}

export enum PreInterviewStep {
    DEVICE_BASIC = 'device-basic',
    AUDIO_SETUP = 'audio-setup',
    VIDEO_SETUP = 'video-setup',
    BEGIN_INTERVIEW = 'begin-interview',
    INTERVIEW_INITIATED = 'interview-initiated',
}

interface PreInterviewStore {
    /** `INTERVIEW_INITIATED` once the candidate begins. Streams opened during the checks are released per step; the interview opens its own. */
    currentStep: PreInterviewStep

    audioSetup: AudioSetup
    videoSetup: VideoSetup

    networkCheck: {
        status: NetworkCheckStatus
        rttMs: number | null
        downloadMbps: number | null
        uploadMbps: number | null
        screenShareRequired: boolean | null
        message: string | null
    }

    checks: {
        livenessRequired: boolean
        livenessCompleted: boolean
    }

    screenSetup: {
        sharingRequired: boolean
        sharingCompleted: boolean
        isSharing: boolean
        stream: RefObject<MediaStream | null>
    }

    setSelectedMic: (mic: AudioSetup['selectedMic']) => void
    setSelectedSpeaker: (speaker: AudioSetup['selectedSpeaker']) => void
    setIsMicCompleted: (isMicCompleted: boolean) => void
    setIsSpeakerCompleted: (isSpeakerCompleted: boolean) => void

    setSelectedCamera: (camera: VideoSetup['selectedCamera']) => void
    setIsCameraCompleted: (isCameraCompleted: boolean) => void
    setVideoStream: (stream: MediaStream | null) => void

    setScreenSharingCompleted: (completed: boolean) => void
    setScreenSharing: (isSharing: boolean, stream?: MediaStream | null) => void
    setScreenSharingRequired: (required: boolean) => void

    /** Mark face liveness (Rekognition) step satisfied for pre-check navigation. */
    setLivenessCompleted: (completed: boolean) => void
    setLivenessRequired: (required: boolean) => void

    setNetworkCheckRunning: () => void
    setNetworkCheckResult: (result: {
        status: 'good' | 'warn' | 'error'
        rttMs: number | null
        downloadMbps: number | null
        uploadMbps: number | null
        screenShareRequired: boolean
        message: string | null
    }) => void
    resetNetworkCheck: () => void

    beginInterview: () => void
}

export const usePreInterviewStore = create<PreInterviewStore>()(
    devtools((set, get) => ({
        currentStep: PreInterviewStep.AUDIO_SETUP,
        audioSetup: {
            isMicCompleted: false,
            isSpeakerCompleted: false,
            selectedMic: null,
            selectedSpeaker: null,
            micGain: 1.0,
            rnnoiseEnabled: true,
        },
        videoSetup: {
            isCameraCompleted: false,
            selectedCamera: null,
            stream: { current: null },
        },

        checks: {
            livenessRequired: false,
            livenessCompleted: false,
        },

        screenSetup: {
            sharingRequired: false,
            sharingCompleted: false,
            isSharing: false,
            stream: { current: null },
        },

        networkCheck: {
            status: 'idle',
            rttMs: null,
            downloadMbps: null,
            uploadMbps: null,
            screenShareRequired: null,
            message: null,
        },

        /*
         * Set the selected microphone
         * @param mic - The microphone to set
         */
        setSelectedMic: (mic: AudioSetup['selectedMic']) => set({ audioSetup: { ...get().audioSetup, selectedMic: mic } }),
        setSelectedSpeaker: (speaker: AudioSetup['selectedSpeaker']) => set({ audioSetup: { ...get().audioSetup, selectedSpeaker: speaker } }),
        setSelectedCamera: (camera: VideoSetup['selectedCamera']) => set({ videoSetup: { ...get().videoSetup, selectedCamera: camera } }),

        /*
         * Set the isMicCompleted
         * @param isMicCompleted - The isMicCompleted to set
         */
        setIsMicCompleted: (isMicCompleted: boolean) =>
            set({
                audioSetup: { ...get().audioSetup, isMicCompleted: isMicCompleted },
            }),
        setIsSpeakerCompleted: (isSpeakerCompleted: boolean) =>
            set({
                audioSetup: {
                    ...get().audioSetup,
                    isSpeakerCompleted: isSpeakerCompleted,
                },
            }),
        setIsCameraCompleted: (isCameraCompleted: boolean) =>
            set({
                videoSetup: {
                    ...get().videoSetup,
                    isCameraCompleted: isCameraCompleted,
                },
            }),

        /*
         * Set the video stream
         * @param stream - The MediaStream object (can be null to clear)
         */
        setVideoStream: (stream: MediaStream | null) => {
            const videoSetup = get().videoSetup
            videoSetup.stream.current = stream
            set({ videoSetup: { ...videoSetup } })
        },

        /*
         * Set the screen sharing completed status
         * @param completed - Whether screen sharing is completed
         */
        setScreenSharingCompleted: (completed: boolean) => {
            set({
                screenSetup: { ...get().screenSetup, sharingCompleted: completed },
            })
        },

        /*
         * Set the screen sharing state and stream
         * @param isSharing - Whether screen sharing is currently active
         * @param stream - The MediaStream object (optional, can be null to clear)
         */
        setScreenSharing: (isSharing: boolean, stream?: MediaStream | null) => {
            const screenSetup = get().screenSetup
            if (stream !== undefined) {
                screenSetup.stream.current = stream
            }
            set({
                screenSetup: {
                    ...screenSetup,
                    isSharing,
                },
            })
        },
        setScreenSharingRequired: (sharingRequired: boolean) =>
            set({
                screenSetup: { ...get().screenSetup, sharingRequired },
            }),

        setLivenessCompleted: (livenessCompleted: boolean) =>
            set({
                checks: { ...get().checks, livenessCompleted },
            }),
        setLivenessRequired: (livenessRequired: boolean) =>
            set({
                checks: { ...get().checks, livenessRequired },
            }),

        setNetworkCheckRunning: () =>
            set({
                networkCheck: {
                    ...get().networkCheck,
                    status: 'running',
                    message: null,
                },
            }),

        setNetworkCheckResult: (result) =>
            set({
                networkCheck: {
                    status: result.status,
                    rttMs: result.rttMs,
                    downloadMbps: result.downloadMbps,
                    uploadMbps: result.uploadMbps,
                    screenShareRequired: result.screenShareRequired,
                    message: result.message,
                },
            }),

        resetNetworkCheck: () =>
            set({
                networkCheck: {
                    status: 'idle',
                    rttMs: null,
                    downloadMbps: null,
                    uploadMbps: null,
                    screenShareRequired: get().screenSetup.sharingRequired,
                    message: null,
                },
            }),

        beginInterview: () => {
            set({ currentStep: PreInterviewStep.INTERVIEW_INITIATED })
        },
    }))
)
